import React from "react";
import "./tnc.css"; // reuse same styling for consistency

export const Pp = () => {
  return (
    <div className="tnc-page">
      <div className="tnc-container">
        <h1>Privacy Policy for LetBabyTalk</h1>
        <h2>Operated by CradleAI (Abu Dhabi, UAE)</h2>

        {/* INTRODUCTION */}
        <p>
          This Privacy Policy describes how CradleAI (“Company,” “we,” “our,” or
          “us”) collects, uses, stores, protects, and shares your personal
          information when you use the LetBabyTalk mobile application (“App”)
          and related services (“Services”). By accessing or using the App, you
          acknowledge that you have read and understood this Privacy Policy. If
          you do not agree with any part of this Policy, you must stop using the
          App immediately.
        </p>

        {/* SECTION 1 */}
        <h3 className="tnc-section-title">1. Scope of This Policy</h3>
        <p>
          This Privacy Policy applies to all users worldwide, including users in
          the European Union, United States, China, Singapore, Southeast Asia,
          the United Arab Emirates, and any other region where the App is
          available. This Policy also applies to sensitive data such as infant
          audio recordings.
        </p>

        {/* SECTION 2 */}
        <h3 className="tnc-section-title">2. Information We Collect</h3>

        <p>We may collect the following categories of information:</p>

        <p>
          <strong>2.1 Information You Provide Directly</strong>
          <br />
          • Infant crying audio recordings
          <br />
          • Infant’s date of birth, age, or age range
          <br />
          • Infant’s gender
          <br />
          • Notes you voluntarily provide about your infant
          <br />
          • Parent/guardian information (name, email, phone number)
          <br />
          • Account credentials (when applicable)
        </p>

        <p>
          <strong>2.2 User-Generated Content (UGC)</strong>
          <br />
          • Notes, feedback, and in-App messages
          <br />
          • Any other information you voluntarily submit
        </p>

        <p>
          <strong>2.3 Information Collected Automatically</strong>
          <br />
          • Device type and model
          <br />
          • Operating system version
          <br />
          • Device identifiers (IDFA, GAID, Android ID)
          <br />
          • IP address
          <br />
          • App usage data, session duration, error logs
          <br />
          • AI inference metadata (prediction scores, timestamps)
        </p>

        <p>
          <strong>2.4 Location Information</strong>
          <br />
          • GPS location (if permitted)
          <br />
          • IP-based approximate location
        </p>

        <p>
          <strong>2.5 Third-Party SDKs</strong>
          <br />
          We may integrate secure third-party tools such as:
          <br />
          • AWS (data hosting)
          <br />
          • Firebase Analytics
          <br />
          • Firebase Crashlytics
          <br />
          • Authentication providers
          <br />
          • Content delivery networks
        </p>

        {/* SECTION 3 */}
        <h3 className="tnc-section-title">3. How We Use Personal Information</h3>

        <p>
          We use your information for:
          <br />• Delivering core App functionality
          <br />• Analyzing infant crying audio to provide insights
          <br />• Personalizing user experience
          <br />• Improving the App and AI models
          <br />• Debugging and optimizing performance
          <br />• Communicating with users
          <br />• Maintaining security and preventing fraud
          <br />• Complying with legal obligations
        </p>

        <p>
          <strong>AI Model Development:</strong> We may use anonymized or
          de-identified data to improve our AI models. Identifiable data will
          only be used for model training with explicit consent. You may opt out
          at any time.
        </p>

        {/* SECTION 4 */}
        <h3 className="tnc-section-title">4. Legal Bases for Processing</h3>
        <p>
          For users in the EU/EEA, we process personal data under:
          <br />• Consent
          <br />• Performance of contract
          <br />• Legitimate interests
          <br />• Compliance with legal obligations
          <br />• Protection of vital interests
        </p>

        {/* SECTION 5 */}
        <h3 className="tnc-section-title">5. Sensitive Data</h3>
        <p>
          LetBabyTalk processes sensitive infant audio data. We apply enhanced
          safeguards including encryption, restricted access, short retention
          periods, and anonymization where possible.
        </p>

        {/* SECTION 6 */}
        <h3 className="tnc-section-title">6. Children’s Privacy</h3>
        <p>
          The App is intended for adults. Children under 13 may not create
          accounts. All infant-related data must be submitted by a parent or
          guardian. Unauthorized child accounts will be deleted immediately.
        </p>

        {/* SECTION 7 */}
        <h3 className="tnc-section-title">7. How We Share Information</h3>

        <p>
          We do not sell personal information. We may share information only:
        </p>

        <p>
          • With trusted service providers (cloud hosting, analytics, crash
          reporting)
          <br />
          • To comply with legal obligations or lawful requests
          <br />
          • As part of a business transfer (merger, acquisition)
          <br />
          • With your explicit consent
        </p>

        {/* SECTION 8 */}
        <h3 className="tnc-section-title">8. International Data Transfers</h3>

        <p>
          • Primary data storage location: AWS Singapore (ap-southeast-1)
          <br />
          • Data may be accessed by CradleAI in UAE and other regions
          <br />
          • EU/EEA transfers rely on Standard Contractual Clauses (SCCs)
          <br />
          • China users consent to cross-border transfers
          <br />
          • We comply with PDPA (Singapore), UAE PDPL, GDPR, CCPA/CPRA, and
          other applicable regulations
        </p>

        {/* SECTION 9 */}
        <h3 className="tnc-section-title">9. Cookies & Tracking Technologies</h3>
        <p>
          We may use cookies, SDK tracking, device analytics, crash logging, and
          A/B testing tools to improve user experience. You may disable cookies,
          though some features may not work properly.
        </p>

        {/* SECTION 10 */}
        <h3 className="tnc-section-title">10. Data Retention</h3>
        <p>
          • Raw infant audio: temporarily processed, deleted unless consented
          <br />
          • Account data: retained until deletion
          <br />
          • Crash logs: 12–36 months
          <br />
          • Analytics: aggregated long-term
          <br />
          • Legal records: retained as required by law
        </p>

        <p>
          Upon account deletion, data is soft-deleted, archived briefly, then
          permanently erased with industry-standard secure deletion.
        </p>

        {/* SECTION 11 */}
        <h3 className="tnc-section-title">11. User Rights</h3>
        <p>
          Depending on your jurisdiction, you may have rights including:
          <br />• Access
          <br />• Correction
          <br />• Deletion
          <br />• Restriction
          <br />• Withdraw consent
          <br />• Data portability (JSON format within 30 days)
          <br />• Objection to automated decision-making
        </p>

        <p>
          To exercise rights, contact: <br />
          <strong>letbabytalk@gmail.com</strong>
        </p>

        {/* SECTION 12 */}
        <h3 className="tnc-section-title">12. Security Measures</h3>
        <p>
          We use encryption (TLS/AES-256), secure AWS infrastructure, access
          controls, audits, and monitoring to safeguard your data. No system is
          completely secure, but we follow industry best practices.
        </p>

        {/* SECTION 13 */}
        <h3 className="tnc-section-title">13. Data Breach Notification</h3>
        <p>
          If a data breach occurs, we will notify affected users and relevant
          authorities within legally required timeframes, unless prohibited by
          law.
        </p>

        {/* SECTION 14 */}
        <h3 className="tnc-section-title">14. Third-Party Links</h3>
        <p>
          The App may contain links to third-party websites. We are not
          responsible for their privacy practices. Users should review their
          privacy policies.
        </p>

        {/* SECTION 15 */}
        <h3 className="tnc-section-title">15. Changes to This Privacy Policy</h3>
        <p>
          We may update this Policy periodically. Material updates will be
          communicated within the App. Continued use after updates indicates
          acceptance.
        </p>

        {/* SECTION 16 */}
        <h3 className="tnc-section-title">16. Contact Information</h3>
        <p>
          {/* CradleAI <br />
          Abu Dhabi, United Arab Emirates <br /> */}
          Email: letbabytalk@gmail.com
        </p>
      </div>
    </div>
  );
};
